<template>
  <div
    :class="[
      'price-override-container',
      { 'price-locked--unsaved': hasUnsavedKpiResults },
      { 'price-locked--disabled': !isPriceOverrideEditable },
    ]"
  >
    <div
      v-if="
        ((hasOverrideHandle || isSmartOverrideHandle) && !isActiveTransitionPeriodOverrideHandle) ||
          item.recalculatingKPIs
      "
      class="revert-button-container"
    >
      <v-btn
        class="revert-button"
        :class="[
          hasUnsavedKpiResults ? 'unsaved-kpis' : '',
          !isPriceOverrideEditable ? 'unsaved-kpis--readonly' : '',
        ]"
        small
        depressed
        icon
        :loading="item.recalculatingKPIs"
        :disabled="item.recalculatingKPIs || !isPriceOverrideEditable"
        @click.stop="handleRevert"
      >
        <v-icon v-once x-small dark>fa fa-undo</v-icon>
      </v-btn>
    </div>
    <v-icon v-if="isActiveTransitionPeriodOverrideHandle" x-small class="transition-period-override"
      >fa fa-snowflake-o</v-icon
    >
    <div class="price-container">
      <div
        :class="{
          'price-value': true,
          'transition-period-override': isActiveTransitionPeriodOverrideHandle,
        }"
      >
        {{ formattedPriceValue }}
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import numberFormats from '@enums/number-formats';

export default {
  name: 'PriceOverrideCellRenderer',

  props: {
    valueParams: {
      type: Object,
      required: true,
    },
    isSmartOverride: {
      type: Function,
      required: true,
    },
    useOverridePrice: {
      type: Function,
      required: true,
    },
    hasOverride: {
      type: Function,
      required: true,
    },
    getOverridePrice: {
      type: Function,
      required: true,
    },
    handleRevertButtonClick: {
      type: Function,
      required: true,
    },
    recalculateKPIWrapper: {
      type: Function,
      required: true,
    },
    isEditable: {
      type: [Boolean, Function],
      default: true,
    },
    priceField: {
      type: String,
      default: 'scenarioPrice',
    },
    isActiveTransitionPeriodOverride: {
      type: Function,
      required: true,
    },
    isSameAsChainPrice: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    // Helper to access parent methods more cleanly
    params() {
      return this.valueParams;
    },

    item() {
      const tsgKey = this.params.colDef.context.tsgKey;
      const itemData = tsgKey
        ? // default to {} to avoid console errors
          get(this.params.data, `toolStoreGroups.${tsgKey}`, {})
        : this.params.data;

      return itemData;
    },

    hasUnsavedKpiResults() {
      return !!(this.item.hasUnsavedKpiResults && this.item.hasPriceChanged);
    },

    isSmartOverrideHandle() {
      return this.isSmartOverride(this.item);
    },

    useOverridePriceHandle() {
      return this.useOverridePrice(this.item);
    },

    hasOverrideHandle() {
      return this.hasOverride(this.item);
    },

    isPriceOverrideEditable() {
      return (
        this.params.context && this.params.context.componentParent && this.isEditable !== false
      );
    },

    formattedPriceValue() {
      if (!this.params || !this.params.data || this.isSameAsChainPrice) {
        return '—';
      }

      const scenarioPrice = get(this.item, this.priceField);
      const overridePrice = this.getOverridePrice(this.item);
      const price = this.useOverridePriceHandle ? overridePrice : scenarioPrice;

      const formattedValue = this.formatNumber({
        number: price,
        format: numberFormats.priceFormat,
        nullAsDash: true,
      });

      return formattedValue;
    },

    isActiveTransitionPeriodOverrideHandle() {
      return this.isActiveTransitionPeriodOverride(this.item);
    },
  },

  methods: {
    handleRevert(event) {
      if (event) {
        event.stopPropagation();
      }

      // Use the recalculateKPIWrapper from the parent component
      this.recalculateKPIWrapper({
        callback: () => this.handleRevertButtonClick(this.item),
        item: this.item,
        rowNode: this.params.node,
        revert: true,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
.price-override-container {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 4px;
}

.price-container {
  display: flex;
  flex: 1;
  justify-content: space-between;
  padding-left: 4px;
}

.price-value {
  flex: 1;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.revert-button-container {
  flex: 0 0 24px;
  height: 20px;
  display: flex;
  align-items: center;
}

.revert-button {
  flex: 0 0 auto;
  margin-right: 4px;
  min-width: 20px;
  width: 20px;
  height: 20px;
  color: white !important;
  background-color: $pricing-primary;
  border-radius: 4px;

  &.unsaved-kpis {
    background-color: $pricing-orange;

    &.theme--light.v-btn.v-btn--disabled {
      background-color: $pricing-orange !important;
    }

    &--readonly {
      background-color: $pricing-grey-dark !important;

      &.theme--light.v-btn.v-btn--disabled {
        background-color: $pricing-grey-dark !important;
      }
    }
  }
}

.transition-period-override {
  color: $grid-view-transition-period-override-color !important;
}
</style>
